import {
  ChevronDownIcon,
  ChevronRightIcon,
  PhoneIcon
} from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Icon,
  IconButton,
  Text,
  Container,
  Heading,
  VStack,
  Divider,
  Avatar,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  TagLabel,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useMemo, useState, useContext } from "react";
import {
  MdExpandLess,
  MdExpandMore,
  MdGroup,
  MdLocationOn,
  MdWork,
  MdBusiness,
  MdDashboard,
  MdArrowBack,
  MdChevronRight,
} from "react-icons/md";
import { FiUsers, FiMapPin, FiPhone, FiTrendingUp } from "react-icons/fi";
import { useLoaderData } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Right,
} from "../../../../components/Card";
import Link from "../../../../components/Link";
import client from "../../../../helpers/Client";
import urlToId from "../../../../helpers/UrlToId";
import AuthContext from "../../../../contexts/Auth";
import ExportButton from "../../../../components/export/ExportButton";
import { useTranslation } from 'react-i18next';

export async function ResellerLoader() {
  const resp = await client.get("/resellers/");
  return [resp.data];
}

const resellerLevels = [
  { color: "red.800", label: "Ana Bayi", level: 0 },
  { color: "green.700", label: "Bölge Bayisi", level: 1 },
  { color: "blue.600", label: "Alt Bayi", level: 2 },
];

const indentColors = resellerLevels.map((b) => b.color);

function getLevelColor(level) {
  const index = Math.min(level, indentColors.length - 1);
  return indentColors[index];
}

function SummaryItem({ icon, children, isVisible = true }) {
  const textColor = 'gray.600';
  const iconColor = 'gray.500';

  if (!isVisible || !children) return null;
  return (
    <HStack spacing={2} fontSize="sm" color={textColor}>
      <Icon as={icon} color={iconColor} boxSize={4} />
      {children}
    </HStack>
  );
}

function Managers({ managers }) {
  const { t } = useTranslation();
  return (
    <Text as="span" title={t('reseller.manager_title')}>
      {managers.map((manager, idx) => (
        <React.Fragment key={manager.url}>
          {manager.first_name} {manager.last_name}
          {/* Display manager's full name */}
          {(manager.email || manager.phone) && " ("}
          {/* Add opening parenthesis if email or phone exists */}
          {manager.email && (
            <Link to={`mailto:${manager.email}`}>{manager.email}</Link>
          )}
          {manager.email && manager.phone_number && ", "}
          {manager.phone_number && (
            <Link to={`tel:${manager.phone_number}`}>
              {manager.phone_number}
            </Link>
          )}
          {(manager.email || manager.phone) && ")"}
          {/* Add closing parenthesis if email or phone exists */}
          {idx < managers.length - 1 && ", "}
          {/* Add comma separator between managers except for the last one */}
        </React.Fragment>
      ))}
    </Text>
  );
}

function ToggleButton({ isOpen, hasChildren, onClick }) {
  const { t } = useTranslation();
  const hoverBg = 'gray.100';
  const iconColor = 'gray.600';

  return (
    <IconButton
      icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
      variant="ghost"
      size="sm"
      onClick={onClick}
      aria-label={isOpen ? t('reseller.collapse') : t('reseller.expand')}
      visibility={hasChildren ? "visible" : "hidden"}
      color={iconColor}
      _hover={{ bg: hoverBg, transform: 'scale(1.1)' }}
      transition="all 0.2s"
      borderRadius="md"
    />
  );
}

function SummaryRow({ reseller }) {
  const { t } = useTranslation();
  return (
    <VStack spacing={3} align="stretch">
      {reseller.phone_number && (
        <SummaryItem icon={FiPhone} isVisible={reseller.phone_number}>
          <Text as="span" title={t('reseller.phone_number_title')} fontWeight="medium">
            {reseller.phone_number}
          </Text>
        </SummaryItem>
      )}
      {reseller.address && (
        <SummaryItem icon={FiMapPin} isVisible={reseller.address}>
          <Text as="span" title={t('reseller.address_title')} fontWeight="medium">
            {reseller.address}
          </Text>
        </SummaryItem>
      )}
      {reseller.managers?.length > 0 && (
        <SummaryItem icon={FiUsers} isVisible={reseller.managers?.length > 0}>
          <Managers managers={reseller.managers} />
        </SummaryItem>
      )}
    </VStack>
  );
}

function ResellerChildren({
  children,
  level,
  isOpen,
  onToggleNode,
  getIsChildExpanded,
}) {
  if (!children || !isOpen) return null;
  return (
    <Box ml={8}>
      {children.map((childReseller) => (
        <ResellerNode
          key={childReseller.url}
          reseller={childReseller}
          level={level + 1}
          parentIsOpen={isOpen}
          isNodeExpanded={getIsChildExpanded(childReseller.url)}
          onToggleNode={onToggleNode}
          getIsChildExpanded={getIsChildExpanded}
        />
      ))}
    </Box>
  );
}

function ResellerNode({
  reseller,
  level = 0,
  parentIsOpen = true,
  isNodeExpanded,
  onToggleNode,
  getIsChildExpanded,
}) {
  const { t } = useTranslation();
  const hasChildren = reseller.children?.length > 0;
  const levelColor = getLevelColor(level);
  const isOpen = isNodeExpanded;

  const cardBg = 'white';
  const borderColor = 'gray.200';
  const hoverBg = 'gray.50';
  const textColor = 'gray.800';
  const summaryBg = 'gray.50';
  const levelBadgeColors = {
    0: { bg: 'red.100', color: 'red.800', label: t('reseller.level_main') },
    1: { bg: 'green.100', color: 'green.800', label: t('reseller.level_region') },
    2: { bg: 'blue.100', color: 'blue.800', label: t('reseller.level_sub') }
  };
  const levelStyle = levelBadgeColors[level] || levelBadgeColors[0];

  const toggleOpen = (e) => {
    e.preventDefault();
    if (onToggleNode) {
      onToggleNode(reseller.url);
    }
  };

  if (!parentIsOpen) {
    return null;
  }

  // Styles for root vs child nodes
  const isRoot = level === 0;

  // Root nodes get the full card treatment
  // Child nodes get a simpler list-item style without borders/shadows to avoid nesting clutter
  const boxStyles = isRoot ? {
    bg: cardBg,
    border: "1px solid",
    borderColor: borderColor,
    borderRadius: "xl",
    shadow: "sm",
    _hover: {
      shadow: 'md',
      bg: hoverBg,
      transform: 'translateY(-1px)',
    }
  } : {
    bg: "transparent",
    border: "none",
    borderRadius: "none",
    shadow: "none",
    _hover: {
      bg: "gray.50",
    },
    pl: 2,
    borderLeft: "2px solid",
    borderLeftColor: levelColor
  };

  return (
    <Box mb={isRoot ? 3 : 1} ml={{ base: level * 2, md: level * 6 }}>
      <Box
        p={isRoot ? 4 : 2}
        transition="all 0.2s"
        {...boxStyles}
        // Override borderLeft for root nodes if needed, but boxStyles handles it
        borderLeftWidth={isRoot && level > 0 ? 4 : boxStyles.borderLeft ? undefined : 0}
        borderLeftColor={isRoot ? levelColor : undefined}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          mb={isOpen ? 3 : 0}
          gap={{ base: 2, md: 0 }}
        >
          <HStack spacing={3}>
            <ToggleButton
              isOpen={isOpen}
              hasChildren={hasChildren}
              onClick={toggleOpen}
            />
            <VStack align="start" spacing={1} flex={1}>
              <HStack spacing={2} wrap="wrap">
                <Link
                  fontSize={isRoot ? "lg" : "md"}
                  fontWeight={isRoot ? "bold" : "medium"}
                  color={textColor}
                  to={`/bayi/${urlToId(reseller.url)}`}
                  _hover={{ color: 'blue.500', textDecoration: 'none' }}
                >
                  {reseller.name}
                </Link>
                {isRoot && (
                  <Badge
                    bg={levelStyle.bg}
                    color={levelStyle.color}
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="full"
                    whiteSpace="nowrap"
                  >
                    {levelStyle.label}
                  </Badge>
                )}
              </HStack>
            </VStack>
          </HStack>

          <HStack spacing={2} alignSelf={{ base: "flex-start", md: "auto" }} pl={{ base: 10, md: 0 }}>
            {reseller.nof_open_tickets > 0 && (
              <Tooltip label={t('reseller.open_tickets')}>
                <Badge
                  display="flex"
                  alignItems="center"
                  bg="orange.100"
                  color="orange.800"
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="sm"
                  fontWeight="bold"
                >
                  <Icon as={MdWork} mr={1} boxSize={4} />
                  {reseller.nof_open_tickets}
                </Badge>
              </Tooltip>
            )}
            {reseller.teams?.length > 0 && (
              <Tooltip label={t('reseller.team_count')}>
                <Badge
                  display="flex"
                  alignItems="center"
                  bg="blue.100"
                  color="blue.800"
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="sm"
                >
                  <Icon as={FiUsers} mr={1} boxSize={4} />
                  {reseller.teams.length}
                </Badge>
              </Tooltip>
            )}
          </HStack>
        </Flex>

        {/* Only show details for expanded items on Desktop, or if explicitly expanded on mobile */}
        {isOpen &&
          (reseller.phone_number ||
            reseller.address ||
            reseller.teams.length > 0) && (
            <Box
              bg={summaryBg}
              borderRadius="lg"
              p={3}
              mt={3}
              display={{ base: "none", md: "block" }} // Hide details on mobile to simplify
            >
              <SummaryRow reseller={reseller} />
            </Box>
          )}
      </Box>

      <ResellerChildren
        children={reseller.children}
        level={level}
        isOpen={isOpen}
        onToggleNode={onToggleNode}
        getIsChildExpanded={getIsChildExpanded}
      />
    </Box>
  );
}

function buildResellerTree(resellers) {
  const resellerMap = new Map();

  // İlk geçiş: Tüm bayileri URL'lerine göre map'e ekle
  resellers.forEach((reseller) => {
    resellerMap.set(reseller.url, {
      ...reseller,
      children: [],
    });
  });

  // İkinci geçiş: Parent-child ilişkilerini kur
  const rootResellers = [];
  resellers.forEach((reseller) => {
    const resellerWithChildren = resellerMap.get(reseller.url);

    if (reseller.parent) {
      const parent = resellerMap.get(reseller.parent);
      if (parent) {
        parent.children.push(resellerWithChildren);
      }
    } else {
      rootResellers.push(resellerWithChildren);
    }
  });

  return rootResellers;
}

function getAllNodeUrls(nodes) {
  let urls = [];
  nodes.forEach((node) => {
    urls.push(node.url);
    if (node.children && node.children.length > 0) {
      urls = urls.concat(getAllNodeUrls(node.children));
    }
  });
  return urls;
}

function MobileResellerView({ resellers }) {
  const { t } = useTranslation();
  const resellerTree = useMemo(() => buildResellerTree(resellers), [resellers]);
  const [viewStack, setViewStack] = useState([]);

  // Current list of items to display
  const currentItems = viewStack.length > 0
    ? viewStack[viewStack.length - 1].children
    : resellerTree;

  // Current parent (for header)
  const currentParent = viewStack.length > 0
    ? viewStack[viewStack.length - 1]
    : null;

  const handleItemClick = (item) => {
    if (item.children && item.children.length > 0) {
      setViewStack([...viewStack, item]);
    }
  };

  const handleBack = () => {
    setViewStack(viewStack.slice(0, -1));
  };

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box>
      {/* Navigation Header */}
      {currentParent && (
        <Flex align="center" mb={4} p={2} bg="gray.100" borderRadius="md" onClick={handleBack} cursor="pointer">
          <Icon as={MdArrowBack} mr={2} />
          <VStack align="start" spacing={0}>
            <Text fontWeight="bold">{currentParent.name}</Text>
          </VStack>
        </Flex>
      )}

      {/* List */}
      <VStack spacing={3} align="stretch">
        {currentItems.map(item => {
          const hasChildren = item.children && item.children.length > 0;
          return (
            <Box
              key={item.url}
              p={4}
              bg={cardBg}
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
              shadow="sm"
              onClick={() => hasChildren && handleItemClick(item)}
              cursor={hasChildren ? "pointer" : "default"}
              _active={{ bg: "gray.50" }}
            >
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={1} flex={1}>
                  <HStack>
                    <Link
                      to={`/bayi/${urlToId(item.url)}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontWeight: "bold", fontSize: "1.1rem" }}
                    >
                      {item.name}
                    </Link>
                  </HStack>
                  <HStack spacing={3} fontSize="sm" color="gray.600">
                    {item.nof_open_tickets > 0 && (
                      <HStack spacing={1}>
                        <Icon as={MdWork} color="orange.500" />
                        <Text>{item.nof_open_tickets}</Text>
                      </HStack>
                    )}
                    {item.teams?.length > 0 && (
                      <HStack spacing={1}>
                        <Icon as={FiUsers} color="blue.500" />
                        <Text>{item.teams.length}</Text>
                      </HStack>
                    )}
                  </HStack>
                </VStack>

                {hasChildren && (
                  <Icon as={MdChevronRight} boxSize={6} color="gray.400" />
                )}
              </Flex>
            </Box>
          );
        })}
        {currentItems.length === 0 && (
          <Text textAlign="center" color="gray.500" py={4}>
            {t('reseller.no_resellers_found') || 'Alt bayi bulunamadı'}
          </Text>
        )}
      </VStack>
    </Box>
  );
}

function TreeView({ resellers }) {
  const { t } = useTranslation();
  const resellerTree = useMemo(() => buildResellerTree(resellers), [resellers]);

  const allUrls = useMemo(() => getAllNodeUrls(resellerTree), [resellerTree]);
  const [expandedNodes, setExpandedNodes] = useState(
    () => new Map(allUrls.map((url) => [url, true]))
  );

  const toggleNode = (url) => {
    setExpandedNodes((prevMap) => {
      const newMap = new Map(prevMap);
      const currentState = newMap.get(url) ?? false;
      newMap.set(url, !currentState);
      return newMap;
    });
  };

  const expandAll = () => {
    setExpandedNodes(new Map(allUrls.map((url) => [url, true])));
  };

  const closeAll = () => {
    setExpandedNodes(new Map(allUrls.map((url) => [url, false])));
  };

  const getIsChildExpanded = (url) => expandedNodes.get(url) ?? true;

  console.log('Reseller Tree:', resellerTree);
  console.log('Total Tasks Calculation:', resellerTree.map(reseller => reseller.nof_open_tickets));

  const buttonBg = 'white';
  const buttonHoverBg = 'blue.50';
  const buttonBorder = 'gray.200';

  return (
    <Box>
      <Flex justify="flex-end" mb={6} flexWrap={{ base: 'wrap', md: 'nowrap' }} gap={{ base: 2, md: 0 }}>
        <ButtonGroup size="sm" spacing="3">
          <Button
            leftIcon={<MdExpandMore />}
            onClick={expandAll}
            bg={buttonBg}
            border="1px solid"
            borderColor={buttonBorder}
            _hover={{ bg: buttonHoverBg, borderColor: 'blue.300' }}
            transition="all 0.2s"
            fontWeight="medium"
          >
            {t('reseller.expand_all')}
          </Button>
          <Button
            leftIcon={<MdExpandLess />}
            onClick={closeAll}
            bg={buttonBg}
            border="1px solid"
            borderColor={buttonBorder}
            _hover={{ bg: buttonHoverBg, borderColor: 'blue.300' }}
            transition="all 0.2s"
            fontWeight="medium"
          >
            {t('reseller.collapse_all')}
          </Button>
        </ButtonGroup>
      </Flex>
      {resellerTree.map((reseller) => (
        <ResellerNode
          key={reseller.url}
          reseller={reseller}
          isNodeExpanded={expandedNodes.get(reseller.url) ?? true}
          onToggleNode={toggleNode}
          getIsChildExpanded={getIsChildExpanded}
        />
      ))}
    </Box>
  );
}

function calculateTotalTasks(resellers) {
  return resellers.reduce((acc, reseller) => {
    const childTasks = reseller.children ? calculateTotalTasks(reseller.children) : 0;
    return acc + (reseller.nof_open_tickets || 0) + childTasks;
  }, 0);
}

function Reseller() {
  const { t } = useTranslation();
  const [resellers] = useLoaderData();
  const auth = useContext(AuthContext);
  const user = auth?.data?.user;
  const canSeeManagerSummary = Boolean(user?.is_superuser || user?.is_staff || user?.is_technical);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const headerBg = "linear(135deg, red.600 0%, red.900 100%)";
  const borderColor = "gray.200";

  const cardBg = 'white';
  const textColor = 'white';

  const resellerTree = useMemo(() => buildResellerTree(resellers), [resellers]);
  const totalTasks = calculateTotalTasks(resellerTree);
  const totalResellers = resellers.length;
  const activeResellers = resellers.filter(r => r.nof_open_tickets > 0).length;

  // Modal state for Manager Summary
  const managerSummaryDisclosure = useDisclosure();
  const noManagerResellers = useMemo(
    () => resellers.filter(r => !r.managers || r.managers.length === 0),
    [resellers]
  );
  const managerCounts = useMemo(
    () => resellers.map(r => ({ name: r.name, url: r.url, count: r.managers?.length || 0 })),
    [resellers]
  );

  const getLevelLabel = (level) => {
    switch (level) {
      case 0: return t('reseller.level_main');
      case 1: return t('reseller.level_region');
      case 2: return t('reseller.level_sub');
      default: return t('reseller.level_sub');
    }
  };

  const exportResellerData = useMemo(() => {
    if (!Array.isArray(resellers)) return [];
    return resellers.map((r) => ({
      [t('reseller.export.columns.reseller')]: r.name,
      [t('reseller.export.columns.level')]: getLevelLabel(r.level),
      [t('reseller.export.columns.phone')]: r.phone_number ?? "",
      [t('reseller.export.columns.address')]: r.address ?? "",
      [t('reseller.export.columns.city')]: r.city_name ?? "",
      [t('reseller.export.columns.open_work_order')]: r.nof_open_tickets ?? 0,
      [t('reseller.export.columns.team_count')]: Array.isArray(r.teams) ? r.teams.length : (r.teams_count ?? 0),
      [t('reseller.export.columns.manager_count')]: Array.isArray(r.managers) ? r.managers.length : (r.manager_count ?? 0),
    }));
  }, [resellers, t]);

  const exportColumns = useMemo(() => ([
    { key: t('reseller.export.columns.reseller'), label: t('reseller.export.columns.reseller') },
    { key: t('reseller.export.columns.level'), label: t('reseller.export.columns.level') },
    { key: t('reseller.export.columns.phone'), label: t('reseller.export.columns.phone') },
    { key: t('reseller.export.columns.address'), label: t('reseller.export.columns.address') },
    { key: t('reseller.export.columns.city'), label: t('reseller.export.columns.city') },
    { key: t('reseller.export.columns.open_work_order'), label: t('reseller.export.columns.open_work_order') },
    { key: t('reseller.export.columns.team_count'), label: t('reseller.export.columns.team_count') },
    { key: t('reseller.export.columns.manager_count'), label: t('reseller.export.columns.manager_count') },
  ]), [t]);

  return (
    <Container maxW={{ base: '100%', lg: '7xl' }} px={{ base: 4, md: 6 }} py={{ base: 4, md: 8 }}>
      <VStack spacing={8} align="stretch">
        {/* Header Section */}
        <Box
          bgGradient={headerBg}
          borderRadius="xl"
          p={{ base: 4, md: 6 }}
          mb={6}
          shadow="lg"
          border="1px"
          borderColor={borderColor}
        >
          <Flex justify="space-between" align="center" mb={6} flexDir={{ base: 'column', md: 'row' }} gap={{ base: 4, md: 0 }}>
            <VStack align="start" spacing={2}>
              <HStack spacing={3}>
                <Icon as={MdBusiness} boxSize={8} color={textColor} />
                <Heading size={{ base: 'lg', md: 'xl' }} color={textColor}>
                  {t('reseller.management_system_title')}
                </Heading>
              </HStack>
              <Text color={textColor} fontSize={{ base: 'md', md: 'lg' }} opacity={0.8}>
                {t('reseller.management_system_subtitle')}
              </Text>
            </VStack>
            <Box
              display={{ base: "grid", md: "flex" }}
              gridTemplateColumns={{ base: "repeat(2, 1fr)", md: "none" }}
              gap={{ base: 4, md: 4 }}
              alignItems="center"
              w={{ base: "full", md: "auto" }}
            >
              <VStack spacing={1} align={{ base: "center", md: "center" }}>
                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={textColor}>
                  {totalResellers}
                </Text>
                <Text fontSize="sm" color={textColor} opacity={0.7} textAlign="center">
                  {t('reseller.total_resellers')}
                </Text>
              </VStack>
              <Divider orientation="vertical" h="50px" display={{ base: 'none', md: 'block' }} />
              <VStack spacing={1} align={{ base: "center", md: "center" }}>
                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color="success.400">
                  {activeResellers}
                </Text>
                <Text fontSize="sm" color={textColor} opacity={0.7} textAlign="center">
                  {t('reseller.active_resellers')}
                </Text>
              </VStack>
              <Divider orientation="vertical" h="50px" display={{ base: 'none', md: 'block' }} />
              <VStack spacing={1} align={{ base: "center", md: "center" }}>
                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color="warning.500">
                  {totalTasks}
                </Text>
                <Text fontSize="sm" color={textColor} opacity={0.7} textAlign="center">
                  {t('reseller.open_work_orders')}
                </Text>
              </VStack>
              <Divider orientation="vertical" h="50px" display={{ base: 'none', md: 'block' }} />
              <HStack spacing={2} gridColumn={{ base: "span 2", md: "auto" }} justify={{ base: "center", md: "flex-start" }}>
                {canSeeManagerSummary && (
                  <Button
                    leftIcon={<FiUsers />}
                    size="sm"
                    variant="outline"
                    colorScheme="whiteAlpha"
                    borderColor="whiteAlpha.700"
                    _hover={{ bg: 'whiteAlpha.200' }}
                    onClick={managerSummaryDisclosure.onOpen}
                  >
                    {t('reseller.manager_summary')}
                  </Button>
                )}
                <ExportButton
                  data={exportResellerData}
                  filename="resellers"
                  columns={exportColumns}
                  sheetName={t('navigation.resellers')}
                />
              </HStack>
            </Box>
          </Flex>
        </Box>
        <Box
          bg={cardBg}
          borderRadius="2xl"
          p={6}
          shadow="lg"
          border="1px solid"
          borderColor={'gray.200'}
        >
          {isMobile ? (
            <MobileResellerView resellers={resellers} />
          ) : (
            <TreeView resellers={resellers} />
          )}
        </Box>
        <Modal
          isOpen={managerSummaryDisclosure.isOpen}
          onClose={managerSummaryDisclosure.onClose}
          size={{ base: 'md', md: 'xl' }}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('reseller.modal_manager_summary_title')}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                <HStack spacing={4}>
                  <Tag size="lg" colorScheme="blue" borderRadius="full">
                    <TagLabel>{t('reseller.modal_total_resellers')}: {totalResellers}</TagLabel>
                  </Tag>
                  <Tag size="lg" colorScheme="green" borderRadius="full">
                    <TagLabel>{t('reseller.modal_total_managers')}: {managerCounts.reduce((a, b) => a + b.count, 0)}</TagLabel>
                  </Tag>
                  <Tag size="lg" colorScheme="red" borderRadius="full">
                    <TagLabel>{t('reseller.modal_resellers_without_manager')}: {noManagerResellers.length}</TagLabel>
                  </Tag>
                </HStack>
                {noManagerResellers.length > 0 && (
                  <Box>
                    <Heading size="sm" mb={2}>{t('reseller.resellers_without_managers_title')}</Heading>
                    <HStack spacing={2} wrap="wrap">
                      {noManagerResellers.map((r) => (
                        <Tag key={r.url} size="md" colorScheme="red" borderRadius="full">
                          <Link to={`/bayi/${urlToId(r.url)}`}>{r.name}</Link>
                        </Tag>
                      ))}
                    </HStack>
                  </Box>
                )}
                <Box>
                  <Heading size="sm" mb={2}>{t('reseller.manager_count_by_reseller')}</Heading>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('reseller.reseller')}</Th>
                          <Th textAlign="right">{t('reseller.manager_count')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {managerCounts.map((item) => (
                          <Tr key={item.url}>
                            <Td>
                              <Link to={`/bayi/${urlToId(item.url)}`}>{item.name}</Link>
                            </Td>
                            <Td isNumeric>
                              <Tag colorScheme={item.count === 0 ? 'red' : 'green'} borderRadius="full">
                                <TagLabel>{item.count}</TagLabel>
                              </Tag>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={managerSummaryDisclosure.onClose}>{t('reseller.close')}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Container>
  );
}

export default Reseller;
